import z from "zod";

import { AddressSchema } from "./address";
import { nullableToOptional } from "./utils";

export const BusinessSchema = z.object({
  createdAt: z.string(),
  id: nullableToOptional(z.number().int()),
  email: nullableToOptional(z.string().email()),
  name: nullableToOptional(z.string()),
  type: nullableToOptional(z.string()),
  image: nullableToOptional(z.string()),
  image_url: nullableToOptional(z.string()),
  description: nullableToOptional(z.string()),
  verified: nullableToOptional(z.boolean()),
  rating: nullableToOptional(z.record(z.string(), z.unknown())),
  // Published overall 1-5 rating; null until rating_count reaches the threshold.
  overall_rating: nullableToOptional(z.number()),
  rating_count: nullableToOptional(z.number().int()),
  user_id: nullableToOptional(z.string()),
  banner_image: nullableToOptional(z.string()),
  banner_image_url: nullableToOptional(z.string()),
  is_active: nullableToOptional(z.boolean()),
  is_blacklisted: nullableToOptional(z.boolean()),
  total_reviews: nullableToOptional(z.number().int()),
  favorite_roles: nullableToOptional(z.array(z.string()).optional()),
  address_formatted: nullableToOptional(z.string()),
  address1: nullableToOptional(z.string()),
  address_city: nullableToOptional(z.string()),
  address_state: nullableToOptional(z.string()),
  address_zipcode: nullableToOptional(z.string()),
  address_latitude: nullableToOptional(z.number()),
  address_longitude: nullableToOptional(z.number()),
});

export type Business = z.infer<typeof BusinessSchema>;

export const AssignmentRowSchema = z.object({
  id: z.number(),
  createdAt: z.string().nullable(),
  updatedAt: z.string(),
  deletedAt: z.string().nullable(),
  shift: z
    .object({
      id: z.number(),
      createdAt: z.string(),
      updatedAt: z.string(),
      deletedAt: z.string().nullable(),
      business: BusinessSchema,
      address: nullableToOptional(AddressSchema),
      location: nullableToOptional(z.string()),
      wage: nullableToOptional(z.number()),
      repeat: z.string().nullable(),
      status: z.enum([
        "DRAFT",
        "PUBLISHED",
        "APPLICATIONS",
        "ASSIGNED",
        "CONFIRMED",
        "IN_PROGRESS",
        "COMPLETED",
        "CANCELLED",
        "NO_SHOW",
      ]),
      latitude: nullableToOptional(z.number()),
      longitude: nullableToOptional(z.number()),
      business_id: z.number(),
      role_name: nullableToOptional(z.string()),
      role_description: nullableToOptional(z.string()),
      wage_unit: nullableToOptional(z.string()),
      start_date: nullableToOptional(z.string()),
      end_date: nullableToOptional(z.string()),
      start_time: nullableToOptional(z.string()),
      end_time: nullableToOptional(z.string()),
      days: nullableToOptional(z.array(z.string())),
      min_rating: nullableToOptional(z.number()),
      located_within: nullableToOptional(z.number()),
      allow_instant_acceptance: z.boolean(),
      require_training: z.boolean(),
    })
    .passthrough(),
  business: BusinessSchema,
  offer: z.any(), // Offer schema if needed
  wage: z.number(),
  business_id: z.number(),
  worker_id: z.string(),
  worker: z.object({
    user_id: z.string(),
    full_name: nullableToOptional(z.string()),
  }),
  start_date_time: z.string(),
  end_date_time: z.string(),
  wage_unit: z.string(),
  assignment_status: z.string(),
});

export type AssignmentRow = z.infer<typeof AssignmentRowSchema>;

export const BusinessShiftListResponseSchema = z.object({
  message: z.string().nullable(),
  success: z.boolean().default(false),
  assignments: z.array(AssignmentRowSchema),
  total: z.number(),
});

export type BusinessShiftListResponse = z.infer<
  typeof BusinessShiftListResponseSchema
>;

export const BusinessProfileResponseSchema = z.object({
  business: BusinessSchema,
  message: z.string().nullable(),
  success: z.boolean(),
  metrics: z.object({
    active_shifts: z.number(),
    rating: z.number(),
    response_time_seconds: z.number(),
    shift_completed: z.number()
  }).nullable().optional()
});

export type BusinessProfileResponse = z.infer<
  typeof BusinessProfileResponseSchema
>;
